import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import Loading from '../../../cors/Loading';
import { toast } from 'sonner';
import ConfirmatorPaymentList from '../../../elements/confirmator_payment_list';
import ConfirmatorExportExcel from "../../../cors/ConfirmatorExportExcel";
import { Clock, ChevronLeft, ChevronRight, Info, FileText } from 'lucide-react';
import { MdOutlineFileDownloadDone, MdAttachMoney } from "react-icons/md";
import { useLanguage } from "../../../context/LanguageProvider";

export default function AdminConfirmatorPayments() {
  const { token, role, roles } = useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingInvoices: 0,
    paidInvoices: 0,
    totalAmount: 0,
  });
  function CardWave({ color }) {
    return (
      <svg
        className="absolute bottom-0 left-0 w-full h-10 opacity-70"
        viewBox="0 0 300 60"
        preserveAspectRatio="none"
      >
        <path
          d="M0,40 C60,10 90,55 150,35 C210,15 250,50 300,25 L300,60 L0,60 Z"
          fill={color}
        />
      </svg>
    );
  }

  function HeroIllustration() {
    return (
      <svg viewBox="0 0 220 160" className="w-44 h-32 md:w-56 md:h-40" aria-hidden="true">
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* soft ground shadow */}
        <ellipse cx="110" cy="148" rx="80" ry="8" fill="#0D9488" opacity="0.08" />

        {/* scattered dots, top right */}
        <circle cx="190" cy="18" r="3" fill="#99F0DE" />
        <circle cx="200" cy="30" r="2" fill="#99F0DE" />
        <circle cx="178" cy="8" r="2" fill="#99F0DE" />

        {/* tilted background card */}
        <g transform="rotate(-4 115 60)">
          <rect x="55" y="8" width="122" height="98" rx="20" fill="#CFF3E8" />
        </g>

        {/* main white panel */}
        <rect x="68" y="24" width="96" height="68" rx="12" fill="#FFFFFF" stroke="#E3F6F0" strokeWidth="1" />

        {/* window dots */}
        <circle cx="82" cy="37" r="3" fill="#F87171" />
        <circle cx="91" cy="37" r="3" fill="#FBBF24" />
        <circle cx="100" cy="37" r="3" fill="#34D399" />

        {/* header bars */}
        <rect x="80" y="48" width="30" height="5" rx="2.5" fill="#0F766E" />
        <rect x="80" y="57" width="18" height="4" rx="2" fill="#99F0DE" />

        {/* chart with filled area + highlighted point */}
        <path d="M78,86 L78,72 L90,60 L102,68 L116,48 L152,58 L152,86 Z" fill="url(#chartFill)" />
        <polyline
          points="78,72 90,60 102,68 116,48 152,58"
          fill="none"
          stroke="#14B8A6"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="116" cy="48" r="3.5" fill="#FFFFFF" stroke="#0D9488" strokeWidth="2.5" />

        {/* floating mini stat card */}
        <g>
          <rect x="8" y="72" width="66" height="46" rx="14" fill="#0D9488" />
          <rect x="18" y="86" width="26" height="6" rx="3" fill="#CFF3E8" />
          <rect x="18" y="97" width="40" height="5" rx="2.5" fill="#5EEAD4" />
          <path d="M50,84 L58,84 L58,100" fill="none" stroke="#5EEAD4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M46,96 L52,90 L58,94 L64,84" fill="none" stroke="#F5C87A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* coin stack */}
        <g>
          <ellipse cx="152" cy="132" rx="17" ry="6" fill="#C9862F" />
          <rect x="135" y="122" width="34" height="10" fill="#EDB35F" />
          <ellipse cx="152" cy="122" rx="17" ry="6" fill="#F5C87A" />
          <ellipse cx="152" cy="122" rx="10" ry="3.5" fill="#FBDFA6" />
        </g>
        <g>
          <ellipse cx="172" cy="120" rx="13" ry="5" fill="#C9862F" />
          <rect x="159" y="112" width="26" height="8" fill="#EDB35F" />
          <ellipse cx="172" cy="112" rx="13" ry="5" fill="#F5C87A" />
          <ellipse cx="172" cy="112" rx="7.5" ry="2.8" fill="#FBDFA6" />
        </g>
      </svg>
    );
  }

  // New states for confirmator selection
  const [showConfirmatorModal, setShowConfirmatorModal] = useState(false);
  const [confirmators, setConfirmators] = useState([]);
  const [selectedConfirmators, setSelectedConfirmators] = useState([]);
  const [loadingConfirmators, setLoadingConfirmators] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchInvoices(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const fetchInvoices = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/confirmators-invoices?page=${page}&search=${search}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setInvoices(data.invoices.data);  
        setCurrentPage(data.invoices.current_page);
        setLastPage(data.invoices.last_page);
        setPerPage(data.invoices.per_page);
        setTotal(data.invoices.total);
        setStats(data.stats); 
        setError(null);
      } else {
        setError(data.message || t('payments.confirmators.fetch_failed'));
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchConfirmators = async () => {
    try {
      setLoadingConfirmators(true);
      const res = await fetch(`${BASE_URL}/api/confirmators-salaries`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (data.code === 200) {
        setConfirmators(data.data || []);
        setSelectedConfirmators([]); 
        setShowConfirmatorModal(true);
      } else {
        toast.error(data.message || t('payments.confirmators.fetch_failed_confirmators'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('payments.confirmators.fetch_error'));
    } finally {
      setLoadingConfirmators(false);
    }
  };

  const generateSalaryInvoices = async () => {
    if (selectedConfirmators.length === 0) {
      toast.warning(t('payments.confirmators.select_required'));
      return;
    }

    try {
      setGeneratingInvoices(true);
      const res = await fetch(`${BASE_URL}/api/confirmator-auto-invoices-salaries`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          confirmator_ids: selectedConfirmators
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(data.message || t('payments.confirmators.generate_success'));
        setShowConfirmatorModal(false);
        setSelectedConfirmators([]);
        fetchInvoices(); // Refresh the invoices list
      } else {
        toast.warning(data.message || t('payments.confirmators.generate_failed'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('payments.confirmators.generate_error'));
    } finally {
      setGeneratingInvoices(false);
    }
  };

  // Handle confirmator selection
  const handleConfirmatorSelect = (confirmatorId) => {
    setSelectedConfirmators(prev => {
      if (prev.includes(confirmatorId)) {
        return prev.filter(id => id !== confirmatorId);
      } else {
        return [...prev, confirmatorId];
      }
    });
  };

  // Select all confirmators
  const handleSelectAll = () => {
    if (selectedConfirmators.length === confirmators.length) {
      setSelectedConfirmators([]);
    } else {
      setSelectedConfirmators(confirmators.map(c => c.id));
    }
  };

  const pagination = {
    currentPage,
    lastPage,
    total,
    from: (currentPage - 1) * perPage + 1,
    to: Math.min(currentPage * perPage, total)
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleCloseModal = () => {
    setShowExportModal(false);
  };

  const handleGeneratePayment = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/confirmator-auto-invoices`, {
        method: 'GET',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json' 
        }
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success(data.message);
        fetchInvoices();
      } else {
        toast.warning(data.message);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('payments.confirmators.error_occurred'));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      fetchInvoices(newPage);
    }
  };

  const prevPage = () => handlePageChange(currentPage - 1);
  const nextPage = () => handlePageChange(currentPage + 1);
  const paginate = (pageNum) => handlePageChange(pageNum);

  const { totalInvoices, pendingInvoices, paidInvoices, totalAmount } = stats;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('payments.confirmators.title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('payments.confirmators.subtitle')}
            </p>
          </div>
          <div className="hidden md:block">
              <HeroIllustration />
          </div>
          {
            role === 1 || (role === 2 && roles.generate_confirmator_invoices === 1) ? (
              <div className="mt-6 md:mt-0 flex gap-4">
                <button 
                  onClick={handleExportClick} 
                  className="cursor-pointer text-xs bg-emerald-500 hover:bg-emerald-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300"
                >
                  {t('payments.confirmators.export_excel')}
                </button>
                {showExportModal && (
                  <ConfirmatorExportExcel 
                    onClose={handleCloseModal}
                  />
                )}
                <button 
                  onClick={fetchConfirmators}
                  disabled={loadingConfirmators}
                  className="cursor-pointer text-xs bg-teal-500 hover:teal-600 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('payments.confirmators.salary_payments')}
                </button>
                <button 
                  onClick={handleGeneratePayment}
                  className="cursor-pointer text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all duration-300"
                >
                  {t('payments.confirmators.generate_payments')}
                </button>
              </div>
            ): ''
          }
        </div>

        {/* Confirmator Selection Modal */}
        {showConfirmatorModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99] p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900">{t('payments.confirmators.modal_title')}</h3>
                  <button
                    onClick={() => setShowConfirmatorModal(false)}
                    className="text-gray-400 cursor-pointer hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6 max-h-96 overflow-y-auto">
                {confirmators.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-gray-600">
                        {t('payments.confirmators.x_of_y_selected', { count: selectedConfirmators.length, total: confirmators.length })}
                      </span>
                      <button
                        onClick={handleSelectAll}
                        className="text-xs bg-gray-100 cursor-pointer hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full"
                      >
                        {selectedConfirmators.length === confirmators.length ? t('payments.confirmators.deselect_all') : t('payments.confirmators.select_all')}
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {confirmators.map((confirmator) => (
                        <label key={confirmator.id} className="flex items-center px-3 py-1.5 border border-gray-200 rounded-2xl hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedConfirmators.includes(confirmator.id)}
                            onChange={() => handleConfirmatorSelect(confirmator.id)}
                            className="h-4 w-4 text-[var(--color-primary-500)] focus:ring-[var(--color-primary-500)] border-gray-300 rounded-2xl"
                          />
                          <div className="ml-3">
                            <span className="text-xs font-medium text-gray-900">{confirmator.name}</span>
                            <span className="block text-[11px] text-gray-500">{confirmator.email}</span>
                            <span className="block text-[11px] text-blue-600">
                              {t('payments.confirmators.unpaid_orders', { count: confirmator.unpaid_orders_count })}
                            </span>
                            
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">{t('payments.confirmators.no_confirmators_found')}</p>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmatorModal(false)}
                  className="px-4 py-1.5 cursor-pointer text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-full"
                >
                  {t('payments.confirmators.cancel')}
                </button>
                <button
                  onClick={generateSalaryInvoices}
                  disabled={selectedConfirmators.length === 0 || generatingInvoices}
                  className="px-4 py-1.5 cursor-pointer text-xs bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {generatingInvoices ? (
                    <>
                      <svg className="animate-spin -ml-1 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t('payments.confirmators.generating')}
                    </>
                  ) : (
                    t('payments.confirmators.generate_invoices_count', { count: selectedConfirmators.length })
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Total invoices */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.total_invoices')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-3xl font-bold text-teal-600">{totalInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-teal-600" />
              </div>
            </div>
            <CardWave color="#CCFBF1" />
          </div>

          {/* Pending invoices */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.pending_invoices')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-3xl font-bold text-yellow-600">{pendingInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <CardWave color="#FEF3C7" />
          </div>

          {/* Paid invoices */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.paid_invoices')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-3xl font-bold text-green-600">{paidInvoices}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MdOutlineFileDownloadDone className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <CardWave color="#D1FAE5" />
          </div>

          {/* Total amount */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  {t('payments.total_amount')}
                  <Info className="w-3.5 h-3.5 text-gray-300" />
                </p>
                <p className="text-2xl font-bold text-blue-600">{totalAmount} MAD</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <MdAttachMoney className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <CardWave color="#DBEAFE" />
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-8">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('payments.confirmators.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full dark:bg-stone-900 dark:text-white outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loading />
          </div>
        )}

        {error && <p className="text-center text-red-500 py-10">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8 flex justify-between items-center">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-xs font-medium">
                {t('payments.confirmators.invoice_found', { count: invoices.length })}
              </span>
            </div>

            {/* Invoices List */}
            {invoices.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-x-auto custom-scrollbar">
                  <div className="min-w-[60rem]">
                    <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="pl-6 pr-2 py-4 text-left text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_invoice')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_confirmator')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_orders')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_amount')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_payment_date')}</th>
                        <th className="px-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_status')}</th>
                        <th className="pr-6 pl-2 py-4 text-center text-[11px] font-medium text-gray-600 uppercase tracking-wider">{t('payments.confirmators.table_actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {invoices.map((invoice, key) => (
                        <ConfirmatorPaymentList key={key} role={role} roles={roles} invoice={invoice} fetchInvoices={fetchInvoices} />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
            ) : (
              /* Empty State */
              <div className="text-center py-0">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('payments.confirmators.no_invoices')}</h3>
                {
                  role === 1 &&
                  <div>
                    <p className="text-gray-600 text-sm mb-6">{t('payments.confirmators.no_invoices_desc')}</p>
                    <button
                      onClick={handleGeneratePayment}
                      className="cursor-pointer text-xs bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold py-2 px-6 rounded-xl transition-all duration-300"
                    >
                      {t('payments.confirmators.generate_invoices_btn')}
                    </button>
                  </div>
                }
              </div>
            )}

            {/* Enhanced Pagination */}
            {lastPage > 1 && (
              <div className="bg-white px-4 py-3 sm:px-6 mt-6 rounded-b-2xl">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    {t('payments.confirmators.pagination', { from: pagination.from, to: pagination.to, total: pagination.total })}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={pagination.currentPage === 1}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                        // Show first pages, current page with neighbors, or last pages
                        let pageNum;
                        if (pagination.lastPage <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                          pageNum = pagination.lastPage - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`px-3 py-1 cursor-pointer text-sm rounded-md ${pagination.currentPage === pageNum ? 'bg-[var(--color-primary-500)] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <button
                          onClick={() => paginate(pagination.lastPage)}
                          className={`px-3 py-1 cursor-pointer text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50`}
                        >
                          {pagination.lastPage}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={nextPage}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === pagination.lastPage ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}